import type { ReactElement } from 'react';
import React, { useCallback } from 'react';
import type { UseComboboxPropGetters } from 'downshift';
import type { VirtualItem } from '@tanstack/react-virtual';

import { MenuItem, MenuItemLoadingIndicator } from './styled-components';

type UseItemMapProps<Item> = {
  getItemProps: UseComboboxPropGetters<Item>['getItemProps'];
  itemRenderer: (item: Item) => ReactElement;
  itemToValue: (item: Item) => string | number;
  items: Item[];
  selectedItem: Item | Item[] | null;
  highlightedIndex: number;
};

export function useItemMap<Item>({
  getItemProps,
  itemRenderer,
  itemToValue,
  items,
  selectedItem,
  highlightedIndex,
}: UseItemMapProps<Item>) {
  return useCallback(
    (virtualRow: VirtualItem) => {
      // virtualized styles
      const style = {
        height: virtualRow.size,
        inset: 0,
        position: 'absolute' as const,
        transform: `translateY(${virtualRow.start}px)`,
      };

      // paginated results render a spinner as the last item
      const isLoadingRow = virtualRow.index > items.length - 1;
      if (isLoadingRow) {
        return (
          <MenuItemLoadingIndicator key="loading-indicator" style={style} />
        );
      }

      // transform consumer data
      const item = items[virtualRow.index];
      const itemValue = itemToValue(item);
      const isSelected = Array.isArray(selectedItem)
        ? !!selectedItem.find((item) => {
            return itemToValue(item) === itemValue;
          })
        : !!selectedItem && itemToValue(selectedItem) === itemValue;

      const downshiftItemProps = getItemProps({
        // NOTE: the `disabled` prop only affects mouse users :(
        // @see https://github.com/downshift-js/downshift/issues/1295
        isSelected,
        index: virtualRow.index,
        item,
        style,
        role: Array.isArray(selectedItem)
          ? 'menuitemcheckbox'
          : 'menuitemradio',
        // downshift expects us to set this manually when using useSelect
        'aria-selected': highlightedIndex === virtualRow.index,
      });

      return (
        <MenuItem
          // Prefer unique key to `virtualRow.index` — avoids issues with
          // React's reconciliation during search.
          key={itemValue}
          {...downshiftItemProps}
        >
          {itemRenderer(item)}
        </MenuItem>
      );
    },
    [items, itemToValue, selectedItem, getItemProps, itemRenderer]
  );
}
